import {
  BuyerModel,
  CampaignOverviewContractModel,
  type IdentityVerification,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxDivider,
  AgxPanel,
  AgxSpacer,
  AgxTypography,
  type BadgeColor,
  Icon_Chevron,
  formatCurrency,
  formatDate,
  formatName,
  getDateStatus,
} from '@urbanx/agx.ui-components2';
import { MenuTitles, Pages } from 'constants/menu';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { splitPascalCase } from 'utils/stringUtils';
import CampaignOverviewInfoCard from './CampaignOverviewInfoCard';
import CampaignOverviewSection from './CampaignOverviewSection';
import ContactDetails from './ContactDetails';
import IdentityVerificationIndicator from './IdentityVerificationIndicator';

interface BuyerContact {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
  identityVerification?: IdentityVerification;
}

interface BuyerSummary {
  name: string;
  contacts: BuyerContact[];
}

interface ContractOverviewProps {
  contract: CampaignOverviewContractModel;
  index: number;
}

function ContractOverview({ contract, index }: ContractOverviewProps) {
  const { agencyId, campaignId } = useParams();
  const navigate = useNavigate();
  const buyers = contract.buyers?.flatMap(getBuyerSummaries) ?? [];
  const keyDates = [
    { label: 'Contract date', date: contract.contractDate },
    { label: 'Finance due', date: contract.financeDueDate },
    { label: 'Building & pest due', date: contract.buildingAndPestDueDate },
    {
      label: contract.actualUnconditionalDate ? 'Unconditional' : 'Expected unconditional',
      date: contract.actualUnconditionalDate ?? contract.expectedUnconditionalDate,
    },
    {
      label: contract.actualSettlementDate ? 'Settled' : 'Settlement',
      date: contract.actualSettlementDate ?? contract.settlementDate,
    },
  ]
    .filter((keyDate): keyDate is { label: string; date: string } => Boolean(keyDate.date))
    .sort((first, second) => first.date.localeCompare(second.date));
  const deposits = [
    { label: 'Initial deposit', amount: contract.initialDeposit },
    { label: 'Balance deposit', amount: contract.balanceDeposit },
  ];
  const buyerSolicitor = contract.buyerSolicitor?.solicitor;
  const buyerSolicitorName = [buyerSolicitor?.contactName, buyerSolicitor?.solicitorName]
    .filter(Boolean)
    .join(' at ');
  const buyersAgent = contract.buyersAgent;
  const buyersAgentName = [buyersAgent?.name, buyersAgent?.agencyName].filter(Boolean).join(' at ');
  const hasBuyersAgent = Boolean(
    buyersAgent?.agencyName || buyersAgent?.name || buyersAgent?.email || buyersAgent?.phone
  );
  const contractPath = `/${Pages.AGENCIES}/${agencyId}/${Pages.CAMPAIGNS}/${campaignId}/${Pages.CONTRACTS}/${contract.contractId}/${Pages.BUYERS_INFORMATION}`;
  const contractNavigationState = { activeTab: MenuTitles.CONTRACTS };

  return (
    <div
      className="campaignOverviewContractCard"
      role="link"
      tabIndex={0}
      aria-label={`Open ${contract.contractName ?? 'contract'}`}
      onClick={() => navigate(contractPath, { state: contractNavigationState })}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === 'Enter') {
          event.preventDefault();
          navigate(contractPath, { state: contractNavigationState });
        }
      }}
    >
      <AgxPanel
        id={`contract-${contract.contractId}`}
        className="campaignOverviewGroup"
        bodyPadding="spacing-3xl"
      >
        <AgxSpacer gap="spacing-3xl">
          <AgxSpacer gap="spacing-lg">
            <AgxSpacer
              direction="horizontal"
              contentAlignment="center"
              contentJustification="space-between"
              gap="spacing-md"
            >
              <AgxTypography
                id={`contract-${contract.contractId}-index`}
                type="body_ui"
                size="small"
                color="var(--text-tertiary)"
              >
                {`Contract ${index + 1}`}
              </AgxTypography>
              <AgxBadge
                type="colourfill"
                colour={getStageColour(contract.stage)}
                size="md"
                text={contract.stage ? splitPascalCase(contract.stage) : 'Status not recorded'}
              />
            </AgxSpacer>
            <AgxTypography
              id={`contract-${contract.contractId}-name`}
              type="body_ui_strong"
              size="medium"
            >
              {contract.contractName ?? 'Contract'}
            </AgxTypography>
          </AgxSpacer>

          <CampaignOverviewInfoCard>
            <AgxSpacer
              className="campaignOverviewSalePrice"
              gap="spacing-md"
              style={{ minWidth: 0 }}
            >
              <AgxTypography
                id={`contract-${contract.contractId}-sale-price-label`}
                type="caption"
                size="medium"
                color="var(--text-tertiary)"
              >
                Sale price
              </AgxTypography>
              <AgxTypography
                id={`contract-${contract.contractId}-sale-price`}
                type="body_ui_strong"
                size="large"
                numberFont
              >
                {contract.salePrice === undefined ? '—' : formatCurrency(contract.salePrice)}
              </AgxTypography>
            </AgxSpacer>
            <AgxDivider variant="horizontal" />
            <div className="campaignOverviewDeposits">
              {deposits.map(({ label, amount }, depositIndex) => (
                <AgxSpacer gap="spacing-md" style={{ minWidth: 0 }} key={label}>
                  <AgxTypography
                    id={`contract-${contract.contractId}-deposit-${depositIndex}-label`}
                    type="caption"
                    size="medium"
                    color="var(--text-tertiary)"
                  >
                    {label}
                  </AgxTypography>
                  <AgxTypography
                    id={`contract-${contract.contractId}-deposit-${depositIndex}`}
                    type="heading"
                    size="x-small"
                    color="var(--text-secondary)"
                    numberFont
                  >
                    {amount === undefined ? '—' : formatCurrency(amount)}
                  </AgxTypography>
                </AgxSpacer>
              ))}
            </div>
          </CampaignOverviewInfoCard>

          <CampaignOverviewSection
            id={`contract-${contract.contractId}-buyers`}
            label="Buyer details"
          >
            {buyers.length ? (
              buyers.map((buyer, buyerIndex) => {
                const directContact =
                  buyer.contacts.length === 1 && !buyer.contacts[0].name
                    ? buyer.contacts[0]
                    : undefined;

                if (directContact) {
                  return (
                    <CampaignOverviewInfoCard key={`${buyer.name}-${buyerIndex}`}>
                      <ContactDetails
                        id={`contract-${contract.contractId}-buyer-${buyerIndex}`}
                        name={buyer.name}
                        email={directContact.email}
                        phone={directContact.phone}
                        badge={
                          <IdentityVerificationIndicator
                            id={`contract-${contract.contractId}-buyer-${buyerIndex}-identity-verification`}
                            identityVerification={directContact.identityVerification}
                          />
                        }
                      />
                    </CampaignOverviewInfoCard>
                  );
                }

                return (
                  <CampaignOverviewInfoCard key={`${buyer.name}-${buyerIndex}`}>
                    <AgxTypography
                      id={`contract-${contract.contractId}-buyer-${buyerIndex}-name`}
                      type="body_ui_strong"
                      size="medium"
                    >
                      {buyer.name}
                    </AgxTypography>
                    {buyer.contacts.map((contact, contactIndex) => (
                      <AgxSpacer
                        gap="spacing-xl"
                        key={`${contact.name ?? 'contact'}-${contactIndex}`}
                      >
                        <AgxDivider variant="horizontal" />
                        <ContactDetails
                          id={`contract-${contract.contractId}-buyer-${buyerIndex}-contact-${contactIndex}`}
                          name={contact.name}
                          role={contact.role}
                          email={contact.email}
                          phone={contact.phone}
                          compact
                          badge={
                            <IdentityVerificationIndicator
                              id={`contract-${contract.contractId}-buyer-${buyerIndex}-contact-${contactIndex}-identity-verification`}
                              identityVerification={contact.identityVerification}
                            />
                          }
                        />
                      </AgxSpacer>
                    ))}
                  </CampaignOverviewInfoCard>
                );
              })
            ) : (
              <CampaignOverviewInfoCard>
                <ContactDetails
                  id={`contract-${contract.contractId}-buyer`}
                  emptyMessage="No buyer details recorded"
                />
              </CampaignOverviewInfoCard>
            )}
          </CampaignOverviewSection>

          <CampaignOverviewSection
            id={`contract-${contract.contractId}-solicitor`}
            label="Buyer's solicitor"
          >
            <CampaignOverviewInfoCard>
              <ContactDetails
                id={`contract-${contract.contractId}-solicitor`}
                name={buyerSolicitorName}
                email={buyerSolicitor?.solicitorEmail}
                phone={buyerSolicitor?.solicitorPhone}
                emptyMessage="No buyer solicitor recorded"
              />
            </CampaignOverviewInfoCard>
          </CampaignOverviewSection>

          {hasBuyersAgent && (
            <CampaignOverviewSection
              id={`contract-${contract.contractId}-buyers-agent`}
              label="Buyer's agent"
            >
              <CampaignOverviewInfoCard>
                <ContactDetails
                  id={`contract-${contract.contractId}-buyers-agent`}
                  name={buyersAgentName}
                  email={buyersAgent?.email}
                  phone={buyersAgent?.phone}
                />
              </CampaignOverviewInfoCard>
            </CampaignOverviewSection>
          )}

          {keyDates.length > 0 && (
            <CampaignOverviewSection id={`contract-${contract.contractId}-dates`} label="Key dates">
              <CampaignOverviewInfoCard>
                <AgxSpacer gap="spacing-xl">
                  {keyDates.map(({ label, date }, dateIndex) => {
                    const status = getDateStatus(date);
                    const dotState =
                      status === 'Today'
                        ? ' campaignOverviewTimelineDotCurrent'
                        : status === 'Overdue'
                          ? ' campaignOverviewTimelineDotPassed'
                          : '';

                    return (
                      <div className="campaignOverviewTimelineRow" key={label}>
                        <span className={`campaignOverviewTimelineDot${dotState}`} />
                        <AgxTypography
                          id={`contract-${contract.contractId}-date-${dateIndex}-label`}
                          type="body_ui"
                          size="small"
                        >
                          {label}
                        </AgxTypography>
                        {status === 'Today' ? (
                          <AgxBadge
                            type="highlighted"
                            colour="yellow"
                            size="md"
                            text={formatDate(date, false)}
                            highlightText="Today"
                          />
                        ) : (
                          <AgxBadge
                            type="colourfill"
                            colour={status === 'Overdue' ? 'grey' : 'orange'}
                            size="md"
                            text={formatDate(date, false)}
                          />
                        )}
                      </div>
                    );
                  })}
                </AgxSpacer>
              </CampaignOverviewInfoCard>
            </CampaignOverviewSection>
          )}

          <AgxSpacer
            direction="horizontal"
            contentJustification="end"
            style={{ marginTop: 'auto' }}
          >
            <Link
              to={contractPath}
              state={contractNavigationState}
              className="campaignOverviewDetailsLink"
              onClick={(event) => event.stopPropagation()}
            >
              <AgxTypography
                id={`contract-${contract.contractId}-details`}
                type="hyperlink"
                size="medium"
                color="currentColor"
              >
                See details
              </AgxTypography>
              <Icon_Chevron direction="right" width={16} height={16} />
            </Link>
          </AgxSpacer>
        </AgxSpacer>
      </AgxPanel>
    </div>
  );
}

function getBuyerSummaries(buyer: BuyerModel): BuyerSummary[] {
  if (buyer.individual) {
    return [
      {
        name: formatName(buyer.individual.name),
        contacts: [
          {
            email: buyer.individual.email,
            phone: buyer.individual.phoneNumber,
            identityVerification: buyer.individual.identityVerification,
          },
        ],
      },
    ];
  }

  if (buyer.company) {
    return [
      {
        name: buyer.company.companyName ?? 'Company buyer',
        contacts: (buyer.company.signatories ?? []).map((signatory, signatoryIndex) => ({
          name: formatName(signatory.name),
          role: `Signatory ${signatoryIndex + 1}`,
          email: signatory.email,
          phone: signatory.phoneNumber,
          identityVerification: signatory.identityVerification,
        })),
      },
    ];
  }

  if (buyer.powerOfAttorney) {
    const principalName = formatName(buyer.powerOfAttorney.principalName);

    return (buyer.powerOfAttorney.attorneys ?? []).map((attorney) => ({
      name: `${formatName(attorney.name)} (Attorney for ${principalName})`,
      contacts: [
        {
          email: attorney.email,
          phone: attorney.phoneNumber,
          identityVerification: attorney.identityVerification,
        },
      ],
    }));
  }

  return [
    {
      name: formatName(buyer.name),
      contacts: [
        {
          email: buyer.email,
          phone: buyer.phoneNumber,
          identityVerification: buyer.identityVerification,
        },
      ],
    },
  ];
}

function getStageColour(stage?: string): BadgeColor {
  if (!stage) return 'lightgrey';
  if (['Accepted', 'Unconditional', 'Settled'].includes(stage)) return 'green';
  if (['Terminated', 'Withdrawn'].includes(stage)) return 'red';

  return 'purple';
}

export default ContractOverview;
